/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  LinearProgress,
} from '@mui/material';
import { DataGrid } from '../../components/common/datagrid/ResponsiveDataGrid';
import { useGridApiRef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import ManufacturingSearchBox from '../../components/manufacturingmng/ManufacturingSearchBox';
import ManufacturingStatusChips from '../../components/manufacturingmng/ManufacturingStatusChips';
import ManufacturingSummary from '../../components/manufacturingmng/ManufacturingSummary';
import ProductionPlanAddDialog from '../../components/manufacturingmng/productionplan/ProductionPlanAddDialog';
import * as manufacturingApi from '../../lib/api/manufacturing';

const ENABLE_MOCK_FALLBACK =
  process.env.REACT_APP_USE_MANUFACTURING_MOCK === 'true';

/**
 * 제조관리 목록 컨테이너 (Plan A 방안)
 * - 프로젝트 목록 + 제조 진행 요약을 한 화면에 표시
 * - 설계/생산/QC 진행률 표시
 * - 생산계획 없는 프로젝트도 표시 (+ 계획 생성 버튼)
 */
const ManufacturingListContainer = ({ pageUrl }) => {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  
  // 상태 관리
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  // 서버가 내려주는 상태별 건수 (현재 페이지가 아니라 검색조건에 걸리는 전체 건수, status 필터 무시).
  // 키는 백엔드 manufacturingStatusCase 가 만드는 값: 미시작/설계중/생산중/출하대기/출하중/완료/종결
  // + 전체/출고완료(별도 집계).
  const [serverStatusCounts, setServerStatusCounts] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    totalCount: 0,
  });
  
  // 필터 기본값
  const [filters, setFilters] = useState({
    statusFilter: '',
    manufacturingStatus: '',
    searchKeyword: 'projectNo',
    searchWords: '',
    searchStrtDt: dayjs().startOf('year'),
    searchEndDt: dayjs().endOf('year'),
  });

  // ===========================================
  // API 호출: 프로젝트 목록 + 제조 진행 요약 조회
  // ===========================================
  const fetchProjectsWithSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page + 1,
        size: pagination.pageSize,
      };
      
      // 상태 필터
      if (filters.statusFilter) {
        params.status = filters.statusFilter;
      }
      
      // 검색어 필터
      if (filters.searchWords) {
        params.keyword = filters.searchWords;
      }
      
      // 날짜 필터
      if (filters.searchStrtDt) {
        params.startDate = dayjs(filters.searchStrtDt).format('YYYY-MM-DD');
      }
      if (filters.searchEndDt) {
        params.endDate = dayjs(filters.searchEndDt).format('YYYY-MM-DD');
      }
      
      console.log('API 요청 파라미터:', params);
      const response = await manufacturingApi.getProjectsWithSummary(params);
      console.log('API 응답:', response.data);
      
      if (response.data.success) {
        const { content, totalElements } = response.data.data;
        
        // 데이터 변환
        const transformedData = (content || []).map((item, index) => ({
          id: item.projectId || `row-${index}`,
          projectId: item.projectId,
          projectNumber: item.projectNumber,
          projectName: item.projectName,
          customerName: item.customerName,
          planCount: item.planCount || 0,
          productionProgress: item.overallProgress || 0,
          shipmentStatus: item.shipmentStatus || 'N',
          manufacturingStatus: item.manufacturingStatus || (item.planCount > 0 ? '진행중' : '미시작'),
          salesDate: item.salesDate || '',
          amount: item.amount || 0,
        }));
        
        setProjects(transformedData);
        setPagination(prev => ({
          ...prev,
          totalCount: totalElements || 0,
        }));
        setServerStatusCounts(response.data.statusCounts || {});
      } else {
        setError(response.data.message || '데이터를 불러오는데 실패했습니다.');
        setProjects([]);
        setPagination(prev => ({ ...prev, totalCount: 0 }));
        setServerStatusCounts({});
      }
    } catch (err) {
      console.error('API 에러:', err);

      // API 에러 시 Mock 데이터로 폴백 (404, 500, 네트워크 에러 등)
      // 운영에서는 ENABLE_MOCK_FALLBACK 이 꺼져 있어(기본 false) 이 분기로 안 빠지고,
      // 실제 에러 메시지가 화면에 그대로 보인다. 이 플래그를 켜면 서버 에러가 "Mock 데이터를
      // 표시합니다" 경고로 가려지니 운영에서는 켜지 않는다.
      const status = err.response?.status;
      const shouldUseMock = status === 404 || status === 500 ||
                           err.code === 'ERR_NETWORK' ||
                           err.code === 'ERR_BAD_REQUEST';

      if (ENABLE_MOCK_FALLBACK && shouldUseMock) {
        console.log('API 미구현 또는 서버 에러 - Mock 데이터 사용');
        loadMockData();
        setError('서버 연결 실패 - Mock 데이터를 표시합니다.');
      } else {
        setError(err.response?.data?.message || '데이터를 불러오는데 실패했습니다.');
        setProjects([]);
        setPagination(prev => ({ ...prev, totalCount: 0 }));
        setServerStatusCounts({});
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  // ===========================================
  // Mock 데이터 (Plan A 방안 - 진행률 포함)
  // ===========================================
  const loadMockData = () => {
    const mockData = [
      {
        id: 'project-uuid-1',
        projectId: 'project-uuid-1',
        projectNumber: 'HC_PJ_2025_02_025',
        projectName: '[조달] 평택시청 행정정보망 ESA(내PC지키미) 구매',
        customerName: '(주)유앤아이소프트',
        planCount: 2,
        productionProgress: 37,
        shipmentStatus: 'N',
        manufacturingStatus: '진행중',
        salesDate: '2025-02-28',
        amount: 713000,
      },
      {
        id: 'project-uuid-2',
        projectId: 'project-uuid-2',
        projectNumber: 'HC_PJ_2025_01_015',
        projectName: '2025년 SKV1타워 1805호 네트워크 구축',
        customerName: '(주)에스제이링크',
        planCount: 3,
        productionProgress: 91,
        shipmentStatus: 'N',
        manufacturingStatus: '출고대기',
        salesDate: '2025-01-15',
        amount: 11600000,
      },
      {
        id: 'project-uuid-3',
        projectId: 'project-uuid-3',
        projectNumber: 'HC_PJ_2025_02_010',
        projectName: '평택시 의회사무국 L2스위치 구매',
        customerName: '평택시의회',
        planCount: 1,
        productionProgress: 100,
        shipmentStatus: 'Y',
        manufacturingStatus: '출고완료',
        salesDate: '2025-02-10',
        amount: 1800000,
      },
      {
        id: 'project-uuid-4',
        projectId: 'project-uuid-4',
        projectNumber: 'HC_PJ_2025_03_001',
        projectName: '신규 프로젝트 - 아직 생산계획 없음',
        customerName: '(주)테스트고객',
        planCount: 0,
        productionProgress: 0,
        shipmentStatus: 'N',
        manufacturingStatus: '미시작',
        salesDate: '2025-03-15',
        amount: 5000000,
      },
    ];
    
    setProjects(mockData);
    setPagination(prev => ({
      ...prev,
      totalCount: mockData.length,
    }));
    // Mock 모드에서도 카드/칩이 0으로만 보이지 않도록, 위 mockData 4건에 맞춘 서버 응답 형태의
    // 상태별 건수를 직접 채워준다(실제 서버 응답을 흉내낸 것, API 응답 그대로는 아님).
    setServerStatusCounts({
      '전체': mockData.length,
      '미시작': 1, // project-uuid-4
      '설계중': 0,
      '생산중': 1, // project-uuid-1 (진행중)
      '출하대기': 1, // project-uuid-2 (출고대기)
      '출하중': 0,
      '완료': 1, // project-uuid-3 (출고완료 = 전부 출하됨)
      '종결': 0,
      '출고완료': 1, // project-uuid-3 (shipmentStatus='Y')
    });
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchProjectsWithSummary();
  }, [pagination.page, pagination.pageSize]);

  // 필터 변경 시 재조회
  useEffect(() => {
    if (pagination.page !== 0) {
      setPagination(prev => ({ ...prev, page: 0 }));
    } else {
      fetchProjectsWithSummary();
    }
  }, [filters.statusFilter, filters.searchWords, filters.searchStrtDt, filters.searchEndDt, filters.manufacturingStatus]);

  // ===========================================
  // 통계 계산
  // ===========================================
  // 서버가 계산한 상태별 건수(serverStatusCounts, 현재 페이지가 아니라 검색조건에 걸리는
  // 전체 건수 — status 필터와 무관)를 화면 칩/카드가 쓰는 라벨 체계로 옮긴다.
  // 서버 키: 미시작/설계중/생산중/출하대기/출하중/완료/종결 (+ 전체, 출고완료)
  // 화면 라벨: 미시작/진행중/출고대기/출고완료/완료/종결
  //  - 진행중 = 설계중 + 생산중 + 출하중 (세 가지 진행 단계를 하나로 묶어서 보여줌)
  //  - 출고대기 = 출하대기 (표기만 다름, 같은 상태)
  //  - 출고완료 = 서버가 shipmentStatus='Y' 기준으로 따로 센 값 (제조 상태 '완료'와는 다른 기준)
  const statusCounts = useMemo(() => {
    const s = serverStatusCounts || {};
    const n = (key) => s[key] || 0;

    return {
      '전체': n('전체'),
      '미시작': n('미시작'),
      '진행중': n('설계중') + n('생산중') + n('출하중'),
      '출고대기': n('출하대기'),
      '출고완료': n('출고완료'),
      '완료': n('완료'),
      '종결': n('종결'),
    };
  }, [serverStatusCounts]);

  const totals = useMemo(() => {
    return {
      total: statusCounts['전체'],
      notStarted: statusCounts['미시작'],
      inProgress: statusCounts['진행중'],
      awaitingShipment: statusCounts['출고대기'],
      shipped: statusCounts['출고완료'],
      completed: statusCounts['완료'],
    };
  }, [statusCounts]);

  // ===========================================
  // 생산 진행률 렌더링
  // ===========================================
  const OverallProgressCell = ({ value }) => {
    const percent = typeof value === 'number' ? value : 0;
    
    const getColor = () => {
      if (percent >= 100) return 'success';
      if (percent >= 50) return 'info';
      if (percent > 0) return 'warning';
      return 'inherit';
    };
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={percent} 
            color={getColor()}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Typography variant="body2" sx={{ minWidth: 35, textAlign: 'right' }}>
          {percent}%
        </Typography>
      </Box>
    );
  };

  // ===========================================
  // 컬럼 정의 (Plan A 방안 - 진행률 포함)
  // ===========================================
  const columns = useMemo(
    () => [
      {
        field: 'projectNumber',
        headerName: '프로젝트번호',
        width: 160,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'projectName',
        headerName: '프로젝트명',
        minWidth: 250,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'customerName',
        headerName: '고객사',
        width: 150,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'planCount',
        headerName: '생산계획',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const count = params.value || 0;
          return (
            <Chip 
              label={count > 0 ? `${count}건` : '없음'} 
              size="small"
              color={count > 0 ? 'primary' : 'default'}
              variant={count > 0 ? 'filled' : 'outlined'}
            />
          );
        },
      },
      {
        field: 'productionProgress',
        headerName: '생산 진행률',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <OverallProgressCell value={params.value} />,
      },
      {
        field: 'shipmentStatus',
        headerName: '출고',
        width: 80,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const shipped = params.value === 'Y';
          return (
            <Chip
              label={shipped ? '완료' : '미출고'}
              size="small"
              color={shipped ? 'success' : 'default'}
              variant={shipped ? 'filled' : 'outlined'}
            />
          );
        },
      },
      {
        field: 'manufacturingStatus',
        headerName: '상태',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const getStatusColor = () => {
            switch (params.value) {
              case '미시작': return 'default';
              case '진행중': return 'primary';
              case '출고대기': return 'warning';
              case '출고완료': return 'success';
              case '종결': return 'error';
              default: return 'default';
            }
          };
          
          return (
            <Chip 
              label={params.value} 
              size="small" 
              color={getStatusColor()}
              variant="filled"
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: '작업',
        width: 120,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const hasPlan = params.row.planCount > 0;
          
          return hasPlan ? (
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleManageClick(params.row);
              }}
            >
              관리
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleCreatePlanClick(params.row);
              }}
            >
              계획 생성
            </Button>
          );
        },
      },
    ],
    [],
  );

  // ===========================================
  // 이벤트 핸들러
  // ===========================================
  
  // 관리 버튼 클릭 - 생산 계획 목록 페이지로 이동
  const handleManageClick = (row) => {
    navigate(`${pageUrl}/${row.projectId}/plans`);
  };

  // + 계획 생성 버튼 클릭
  const handleCreatePlanClick = (row) => {
    setSelectedProjectId(row.projectId);
    setCreateModalOpen(true);
  };

  // 행 클릭
  const handleRowClick = useCallback((param, event) => {
    const row = param.row;
    if (row.planCount > 0) {
      navigate(`${pageUrl}/${row.projectId}/plans`);
    }
  }, [navigate, pageUrl]);

  // 필터 변경
  const handleFilters = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // 페이지네이션 변경
  const handlePaginationModelChange = useCallback((newModel) => {
    setPagination(prev => ({
      ...prev,
      page: newModel.page,
      pageSize: newModel.pageSize,
    }));
  }, []);

  // 모달 닫기
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setSelectedProjectId(null);
  };

  // 생산 계획 저장
  const handleSavePlan = async (planData) => {
    setCreateLoading(true);
    try {
      const createData = {
        projectId: selectedProjectId,
        
        // 제품 정보
        productId: planData.productUuid || null,
        productName: planData.productName || null,
        
        // 수량/일정
        // 화면에서 받은 생산 수량을 그대로 보낸다. 예전에는 targetQuantity 를 1 로 박아두고
        // planQty 를 아예 안 보내서, 이 경로로 만든 계획은 수량을 뭘 넣든 1 로 저장됐다.
        // 그러면 생산량 KPI 도 출하 수량도 전부 틀어진다.
        planQty: planData.planQty ? parseInt(planData.planQty, 10) : null,
        targetQuantity: planData.planQty ? parseInt(planData.planQty, 10) : null,
        planStartDate: planData.planStartDate || dayjs().format('YYYY-MM-DD'),
        planEndDate: planData.expectedCompletionDate || dayjs().add(1, 'month').format('YYYY-MM-DD'),
        
        // 담당자 정보 (계획 담당자 = 현재 사용자)
        managerUuid: planData.supervisorId || null,
        managerName: planData.supervisor || null,
        designManagerUuid: planData.designerUuid || null,
        designManagerName: planData.designerName || null,
        productionManagerUuid: planData.productionManagerUuid || null,
        productionManagerName: planData.productionManagerName || null,
        qcManagerUuid: planData.qcManagerUuid || null,
        qcManagerName: planData.qcManagerName || null,
        
        // 워크플로우
        // 다이얼로그는 공정 워크플로우를 필수로 받는데(yup required) 이 경로는 그 값을 아예
        // 안 보내고 있었다. 그래서 여기서 만든 계획은 WORKFLOW_UUID 가 NULL 로 남고 라우팅도
        // 안 생겨서, 공정을 하나도 진행할 수 없는 껍데기 계획이 됐다.
        workflowUuid: planData.workflowUuid || null,

        // 기타
        remarks: planData.remarks || null,
        
        addReason: planData.addReason || null,
      };
      
      console.log('생산 계획 생성 요청:', createData);
      const response = await manufacturingApi.createManufacturingPlan(createData);
      
      if (response.data.success) {
        const savedPlanId = response.data.data?.planId || response.data.data?.planUuid;

        // 워크플로우 기반 라우팅 생성. 계획만 만들고 라우팅을 안 만들면 공정 진행을 시작할 수
        // 없다. ProjectPlansContainer 의 생성 경로와 동작을 맞춘다.
        if (planData.workflowUuid && savedPlanId) {
          try {
            const routingResponse = await manufacturingApi.createRoutingFromWorkflow({
              planId: savedPlanId,
              workflowUuid: planData.workflowUuid,
              productUuid: planData.productUuid || null,
            });
            console.log('라우팅 생성 응답:', routingResponse.data);
          } catch (routingError) {
            console.error('라우팅 생성 실패:', routingError);
            alert('생산계획은 생성되었으나, 워크플로우 라우팅 생성에 실패했습니다. 상세 화면에서 수정해주세요.');
          }
        }

        alert('생산 계획이 생성되었습니다.');
        handleCloseCreateModal();
        fetchProjectsWithSummary(); // 목록 재조회
      }
    } catch (err) {
      console.error('생산 계획 생성 에러:', err);
      alert(err.response?.data?.message || '생산 계획 생성에 실패했습니다.');
    } finally {
      setCreateLoading(false);
    }
  };

  // ===========================================
  // Formik 설정
  // ===========================================
  const validationSchema = yup.object({
    manufacturingStatus: yup.string().nullable(),
    searchKeyword: yup.string().nullable(),
    searchWords: yup.string().trim().nullable(),
    searchStrtDt: yup.date().nullable(),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .nullable(),
  });

  const formik = useFormik({
    initialValues: {
      manufacturingStatus: filters.manufacturingStatus,
      searchKeyword: filters.searchKeyword,
      searchWords: filters.searchWords,
      searchStrtDt: filters.searchStrtDt,
      searchEndDt: filters.searchEndDt,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setFilters(prev => ({
        ...prev,
        manufacturingStatus: values.manufacturingStatus,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
      }));
    },
  });

  const handleReset = () => {
    setFilters({
      statusFilter: '',
      manufacturingStatus: '',
      searchKeyword: 'projectNo',
      searchWords: '',
      searchStrtDt: dayjs().startOf('year'),
      searchEndDt: dayjs().endOf('year'),
    });
    formik.resetForm();
  };

  // ===========================================
  // 렌더링
  // ===========================================
  return (
    <>
      <BreadcrumbsContainer
        title="생산/프로젝트별 제조"
        icon="production"
      />

      {/* 상단 헤더 */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          프로젝트별 제조 현황
        </Typography>
      </Box>

      <ManufacturingSearchBox 
        formik={formik} 
        onReset={handleReset}
      />

      <ManufacturingStatusChips 
        filters={filters}
        onFilters={handleFilters}
        statusCounts={statusCounts}
      />

      <ManufacturingSummary totals={totals} />

      {error && (
        <Box sx={{ p: 2 }}>
          <Alert 
            severity={error.includes('Mock') ? 'warning' : 'error'} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Box>
      )}

      <Box sx={{ p: 2 }}>
        <Paper elevation={1}>
          <Box sx={{ height: 600, width: '100%', position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <DataGrid
              apiRef={apiRef}
              rows={projects}
              columns={columns}
              pagination
              paginationMode="server"
              rowCount={pagination.totalCount}
              paginationModel={{
                page: pagination.page,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              loading={loading}
              getRowClassName={(params) => 
                params.row.planCount === 0 ? 'no-plan-row' : ''
              }
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .no-plan-row': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
                '& .no-plan-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* 생산 계획 생성 모달 */}
      <ProductionPlanAddDialog
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSave={handleSavePlan}
        selectedPlan={null}
        projectId={selectedProjectId}
        loading={createLoading}
      />
    </>
  );
};

export default ManufacturingListContainer;
